import { ArtManifest, ExtractedFrame, PackedTexture } from './types.ts';

// ---------------------------------------------------------------------------
// Types (match server zipService.ts)
// ---------------------------------------------------------------------------

export interface MultiatlasJSON {
  textures: Array<{
    image: string;
    format: string;
    size: { w: number; h: number };
    scale: number;
    frames: Array<{
      filename: string;
      frame: { x: number; y: number; w: number; h: number };
      rotated: boolean;
      trimmed: boolean;
      spriteSourceSize: { x: number; y: number; w: number; h: number };
      sourceSize: { w: number; h: number };
    }>;
  }>;
}

export interface AnimsJSON {
  anims: Array<{
    key: string;
    type: string;
    frames: Array<{ key: string; frame: string }>;
    frameRate: number;
    repeat: number;
    yoyo?: boolean;
    repeatDelay?: number;
  }>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const KNOWN_FACINGS = [
  'south', 'southeast', 'east', 'northeast', 'north', 'northwest', 'west', 'southwest',
];

function getOutputKeyFromAnimFacingKey(animFacingKey: string): string {
  for (const facing of KNOWN_FACINGS) {
    const suffix = `_${facing}`;
    if (animFacingKey.endsWith(suffix)) {
      return animFacingKey.slice(0, animFacingKey.length - suffix.length);
    }
  }
  return animFacingKey;
}

// ---------------------------------------------------------------------------
// Public API (ported from server zipService.ts — logic is identical)
// ---------------------------------------------------------------------------

export function generateMultiatlasJSON(packedTextures: PackedTexture[], prefix: string): MultiatlasJSON {
  return {
    textures: packedTextures.map((pt, index) => ({
      image: `${prefix}_${index}.png`,
      format: 'RGBA8888',
      size: { w: pt.width, h: pt.height },
      scale: 1,
      frames: pt.frames.map(pf => {
        const { frame: f } = pf;
        return {
          filename: f.name,
          frame: { x: pf.x, y: pf.y, w: f.width, h: f.height },
          rotated: false,
          trimmed: f.trim.trimmed,
          spriteSourceSize: { x: f.trim.trimX, y: f.trim.trimY, w: f.trim.trimWidth, h: f.trim.trimHeight },
          sourceSize: { w: f.trim.originalWidth, h: f.trim.originalHeight },
        };
      }),
    })),
  };
}

export function generateAnimsJSON(
  outputKeyToFolder: Map<string, string>,
  allFrames: ExtractedFrame[],
  manifest: ArtManifest,
  prefix: string,
): AnimsJSON {
  // Group frames by outputKey_facing
  const groups = new Map<string, ExtractedFrame[]>();
  for (const frame of allFrames) {
    const withoutIndex = frame.name.slice(0, frame.name.lastIndexOf('_'));
    if (!groups.has(withoutIndex)) groups.set(withoutIndex, []);
    groups.get(withoutIndex)!.push(frame);
  }

  for (const frames of groups.values()) {
    frames.sort((a, b) => {
      const ai = parseInt(a.name.slice(a.name.lastIndexOf('_') + 1), 10);
      const bi = parseInt(b.name.slice(b.name.lastIndexOf('_') + 1), 10);
      return ai - bi;
    });
  }

  const anims: AnimsJSON['anims'] = [];

  for (const [animFacingKey, frames] of groups) {
    const outputKey = getOutputKeyFromAnimFacingKey(animFacingKey);
    const animFolder = outputKeyToFolder.get(outputKey);

    let settings = { frameRate: 10, repeat: -1, yoyo: false, repeatDelay: 0 };
    if (animFolder) {
      const animData = manifest.animations[animFolder];
      if (animData) settings = { ...settings, ...animData.defaultSettings };
    }

    const animEntry: AnimsJSON['anims'][number] = {
      key: animFacingKey,
      type: 'frame',
      frames: frames.map(f => ({ key: prefix, frame: f.name })),
      frameRate: settings.frameRate,
      repeat: settings.repeat,
    };
    if (settings.yoyo) animEntry.yoyo = true;
    if (settings.repeatDelay > 0) animEntry.repeatDelay = settings.repeatDelay;

    anims.push(animEntry);
  }

  return { anims };
}
